/**
 * src/components/Financial/Modules/GannSquareModule.jsx
 */
import React, { useMemo } from "react";
import { Target, Info, ArrowUpCircle, ArrowDownCircle } from "lucide-react";

// --- 計算ロジック (Gann理論 修正版) ---
/**
 * Gann Square of Nine Calculation (Corrected)
 * * 理論式: Price = (sqrt(BasePrice) +/- (Increase * (Angle / 360)))^2
 * * 変更点:
 * 1. 分母を360に変更し、回転数(Rotations)を基準とする
 * 2. increase(係数)は「1回転あたりに平方根に加算する値」として定義
 * - 標準的なGann理論では1回転で+2だが、市場に合わせて調整可能にする
 * - 例: FXなら 0.002, 株価なら 2.0 など
 */
const calculateGannSquareLevels = (
  basePrice,
  increase,
  direction,
  rotations = 10
) => {
  // ベース価格のバリデーション
  if (!basePrice || basePrice <= 0) return [];

  const levelsWithAngles = [];
  const startRoot = Math.sqrt(basePrice);
  
  // 係数 (Increase) が 0 の場合は計算不可のためデフォルト値(最小単位)を設定
  // ユーザーが0を入力した際のフリーズ防止
  const coeff = (increase === 0 || increase === "0") ? 0.0001 : parseFloat(increase);
  const dirMult = direction === "down" ? -1 : 1;

  // --- 中心点 (Start: 0度) ---
  levelsWithAngles.push({ price: basePrice, angle: 0, label: "Start" });

  // 45度刻みで計算 (1周 = 8ステップ)
  const stepsPerRotation = 8;
  const totalSteps = rotations * stepsPerRotation;

  for (let i = 1; i <= totalSteps; i++) {
    // 累積角度
    const totalAngle = i * 45;
    
    // 回転率 (Rotations) = 角度 / 360
    // 例: 180度 = 0.5, 360度 = 1.0
    const rotationFactor = totalAngle / 360;

    // デルタ計算: 係数 × 回転率
    // これにより「係数」は「360度回った時の平方根の増加量」を意味するようになる
    const delta = coeff * rotationFactor;

    // ルート値の計算: (√Base ± delta)
    const rootValue = startRoot + (dirMult * delta);

    // 下降トレンドでルート内が負になる場合は計算終了
    if (rootValue < 0) break;

    // 二乗して価格に戻す
    const price = rootValue * rootValue;
    
    // 角度の正規化 (表示用 0-360)
    const normalizedAngle = totalAngle % 360;
    const displayAngle = normalizedAngle === 0 ? 360 : normalizedAngle;

    levelsWithAngles.push({
      price: price,
      angle: displayAngle,
      totalAngle: totalAngle
    });
  }

  // 価格順にソート
  return levelsWithAngles.sort((a, b) => b.price - a.price);
};

// --- 表示レイヤー ---
export const GannSquareLayer = function (props) {
  const { show, coords, basePrice, increase, direction, isDark } = props;
  const basePriceNum = parseFloat(basePrice);
  const increaseNum = parseFloat(increase);

  // バリデーション
  if (!show || !coords || isNaN(basePriceNum) || isNaN(increaseNum))
    return null;

  const gannLevels = useMemo(() => {
    return calculateGannSquareLevels(
      basePriceNum,
      increaseNum,
      direction || "up",
      10 // 10周分計算
    );
  }, [basePriceNum, increaseNum, direction]);

  const getAngleColor = function (angle) {
    var a = Math.abs(angle) % 360;
    if (a === 0 || a === 360) return "#14b8a6"; // Cyan (Start/Cycle)
    if (a === 45) return "#f472b6"; // Pink
    if (a === 90) return "#a855f7"; // Purple (Cardinal)
    if (a === 135) return "#3b82f6"; // Blue
    if (a === 180) return "#94a3b8"; // Slate (Opposition)
    if (a === 225) return "#eab308"; // Yellow
    if (a === 270) return "#f97316"; // Orange (Cardinal)
    if (a === 315) return "#22c55e"; // Green
    return "#6366f1";
  };

  return (
    <g>
      {gannLevels.map(function (l, i) {
        // coords.getY が関数として存在するか確認
        if (typeof coords.getY !== "function") return null;
        
        const yPos = coords.getY(l.price);
        
        // 描画バッファ (画面外でも極端に遠くなければ描画)
        if (isNaN(yPos) || yPos < -2000 || yPos > coords.iH + 2000) return null;

        const typeLabel = l.price >= basePriceNum ? "(R) " : "(S) ";
        const lineColor = getAngleColor(l.angle);
        
        // 重要なアングル (Cardinal & Fixed Cross) は太く
        const isMajor = l.angle % 90 === 0 || l.angle === 0;
        const strokeW = isMajor ? 1.5 : 0.8;
        const dashArray = isMajor ? "4 2" : "2 4";

        // 価格フォーマット（桁数自動調整）
        const formattedPrice = l.price.toLocaleString(undefined, {
            minimumFractionDigits: l.price < 500 ? 2 : 0,
            maximumFractionDigits: l.price < 10 ? 5 : 2
        });

        const labelString = `${typeLabel}${l.angle}° : ${formattedPrice}`;

        return (
          <g key={"gann-lvl-" + i}>
            <line
              x1={coords.pad.l}
              y1={yPos}
              x2={coords.actualWidth - coords.pad.r}
              y2={yPos}
              stroke={lineColor}
              strokeWidth={strokeW}
              strokeDasharray={dashArray}
              opacity="0.8"
            />
            <text
              x={coords.actualWidth - coords.pad.r - 5}
              y={yPos - 5}
              fill={lineColor}
              fontSize="10"
              fontWeight="bold"
              textAnchor="end"
              style={{ textShadow: isDark ? "0px 0px 2px #000" : "none" }}
            >
              {labelString}
            </text>
          </g>
        );
      })}
    </g>
  );
};

// --- 設定UI ---
export const GannSquareSettingsUI = function (props) {
  const {
    show,
    setShow,
    basePrice,
    setBasePrice,
    increase,
    setIncrease,
    direction,
    setDirection,
    isDarkMode,
  } = props;

  const isDark = (function () {
    var p = isDarkMode;
    return p === true || p === "true" || p === "dark";
  })();

  const btnSt = (active) => ({
    padding: "6px 15px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    cursor: "pointer",
    background: active ? "#2c2d96" : isDark ? "#2d2c33" : "#ffffff",
    color: active ? "#fff" : isDark ? "#ccc" : "#64748b",
    border:
      "1px solid " + (active ? "#2c2d96" : isDark ? "#374151" : "#cbd5e1"),
    display: "flex",
    alignItems: "center",
    gap: "5px",
    transition: "all 0.2s",
  });

  return (
    <div
      style={{
        background: isDark ? "#1c1f26" : "#f8fafc",
        padding: "20px",
        borderRadius: "12px",
        border: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
        marginBottom: "15px",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <div
          style={{
            fontSize: "15px",
            fontWeight: "bold",
            color: isDark ? "#ffffff" : "#2c2d96",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Target size={18} /> <span>Gann Square of Nine  (Dynamic Sqrt Model)</span>
        </div>
        <button onClick={() => setShow(!show)} style={btnSt(show)}>
          {show ? "ON" : "OFF"}
        </button>
      </div>

      <div
        style={{
          opacity: show ? 1 : 0.5,
          pointerEvents: show ? "auto" : "none",
        }}
      >
        <div style={{ display: "flex", gap: "15px", flexWrap: "wrap" }}>
          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              基準価格 (Base)
            </label>
            <input
              type="number"
              step="any"
              value={basePrice}
              onChange={(e) => setBasePrice(e.target.value)}
              style={{
                padding: "8px",
                background: isDark ? "#0b0e14" : "#ffffff",
                color: isDark ? "#ffffff" : "#333333",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "6px",
                width: "110px",
              }}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              係数 (Inc/360°)
            </label>
            <input
              type="number"
              step="any"
              value={increase}
              onChange={(e) => setIncrease(e.target.value)}
              placeholder="例: 2, 0.002"
              style={{
                padding: "8px",
                background: isDark ? "#0b0e14" : "#ffffff",
                color: isDark ? "#ffffff" : "#333333",
                border: "1px solid " + (isDark ? "#374151" : "#cbd5e1"),
                borderRadius: "6px",
                width: "100px",
              }}
              title="360度(1周)あたりの平方根加算値。標準は2。FX等は0.002など。"
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
            <label
              style={{
                fontSize: "11px",
                color: isDark ? "#9ca3af" : "#64748b",
              }}
            >
              方向
            </label>
            <div style={{ display: "flex", gap: "5px" }}>
              <button
                onClick={() => setDirection("up")}
                style={btnSt(direction === "up")}
              >
                <ArrowUpCircle size={14} /> 上昇
              </button>
              <button
                onClick={() => setDirection("down")}
                style={btnSt(direction === "down")}
              >
                <ArrowDownCircle size={14} /> 下降
              </button>
            </div>
          </div>
        </div>

        {/* --- 説明テキストエリア --- */}
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: "6px",
            color: isDark ? "rgba(255, 255, 255, 0.5)" : "#64748b",
            fontSize: "10px",
            marginTop: "15px",
            paddingTop: "10px",
            borderTop: "1px solid " + (isDark ? "#2d313d" : "#e2e8f0"),
            lineHeight: "1.4"
          }}
        >
          <Info size={14} style={{ minWidth: "14px", marginTop: "2px" }} />
          <span>
            <b>計算式:</b> (√Base ± (係数 × Angle/360))² <br/>
            ※係数は「1周あたりの平方根増加量」です。(標準理論=2, 為替=0.002程度推奨)
          </span>
        </div>
      </div>
    </div>
  );
};